import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMediaQuery } from '@mui/material'

/**
 * VERA-1095 — cross-fade between sign-in and sign-up, matching the signup
 * stepper's step transition.
 *
 * WHY THIS EXISTS. Sign-in and sign-up are separate ROUTES, so the default
 * behaviour is a hard cut: the outgoing page unmounts in one frame and the
 * incoming one mounts. Sign-up already faded IN over 150ms, but nothing faded
 * OUT, so the eye caught the swap as a flash rather than a movement — the same
 * defect the stepper hit when its wrapper snapped to a new height while the
 * content faded smoothly.
 *
 * WHAT MAKES IT WORK. The two pages already share an identical ambient layer:
 * both render `SignInBackground` as a fixed sibling OUTSIDE the content column,
 * and both hardcode `#000000` (rather than `background.default`'s `#09090B`)
 * specifically so the shell does not flash between them. So the backdrop is
 * genuinely continuous across the route change, and only the CONTENT needs to
 * move. That is the same situation the stepper is in — panels changing over a
 * fixed shell — which is why the same treatment transfers.
 *
 * THE MOTION, copied deliberately from the stepper rather than re-invented:
 * outgoing content leaves moving UP (`translateY(-10px)`, opacity → 0);
 * incoming content enters moving up FROM BELOW (`translateY(10px)` → 0). Both
 * ~200ms `ease`. The direction is constant regardless of which way you
 * navigate, exactly as the stepper's panels do it — so sign-in → sign-up and
 * sign-up → sign-in read as the same gesture, not as forward/back.
 *
 * `prefers-reduced-motion`: navigate IMMEDIATELY, with no delay and no
 * transform. Note this is the opposite trade-off from a plain cross-fade — the
 * stepper keeps an opacity-only fade because its panels swap in place with
 * nothing else to orient the user. Here a route change is already a full
 * context switch, and holding a user 200ms for an animation they asked not to
 * see is a worse outcome than an instant swap.
 */

/** Shared with the incoming pages' mount fade so both halves stay in step. */
export const AUTH_TRANSITION_MS = 200

/**
 * The COLD-LOAD entrance — a direct hit on /sign-in or /sign-up, where there is
 * no outgoing page to cross-fade from.
 *
 * Shorter than AUTH_TRANSITION_MS and opacity-only at the call site, for a
 * reason specific to a cold load: the ambient backdrop (`SignInBackground`) is a
 * fixed sibling OUTSIDE the animated content column — deliberately, so it stays
 * continuous across a sign-in <-> sign-up route change — and therefore has no
 * entrance of its own. On a cold load it paints in the same frame the app mounts
 * while the card fades, so the gap between the two IS the "staging" that
 * `c2264a5ce` removed the cold fade to avoid. Keep this under ~150ms and the two
 * read as one arrival.
 *
 * It is a separate constant rather than a second use of AUTH_TRANSITION_MS
 * because the two answer to different things: that one must equal the OUTGOING
 * page's fade (they are halves of one cross-fade and drift reads as a flash),
 * this one must stay under the eye's threshold for separating two events.
 * Tying them together would mean a future change to the cross-fade silently
 * lengthening the cold entrance past the point this is choosing.
 */
export const AUTH_COLD_ENTER_MS = 150

export interface AuthPageTransition {
  /** True once an exit has started. Drive opacity/transform from this. */
  isLeaving: boolean
  /** Fade the current page out, then navigate. Safe to call twice. */
  leaveTo: (route: string) => void
  /** `sx` for the CONTENT column — never for the ambient background layer. */
  exitSx: {
    opacity: number
    transform: string
    transition: string
  }
}

export const useAuthPageTransition = (): AuthPageTransition => {
  const navigate = useNavigate()
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const [isLeaving, setIsLeaving] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // A pending navigate() must not fire after unmount — React would warn, and on
  // a fast double-navigation it would yank the user off a page they had already
  // moved on from.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  const leaveTo = useCallback(
    (route: string) => {
      if (prefersReducedMotion) {
        navigate(route)
        return
      }
      // Guard the double-click: without this, a second click restarts the timer
      // and the page sits faded-out for longer than the animation it is showing.
      if (timer.current) return
      setIsLeaving(true)
      timer.current = setTimeout(() => navigate(route), AUTH_TRANSITION_MS)
    },
    [navigate, prefersReducedMotion],
  )

  return {
    isLeaving,
    leaveTo,
    exitSx: {
      opacity: isLeaving ? 0 : 1,
      transform: isLeaving ? 'translateY(-10px)' : 'translateY(0)',
      transition: prefersReducedMotion
        ? 'none'
        : `opacity ${AUTH_TRANSITION_MS}ms ease, transform ${AUTH_TRANSITION_MS}ms ease`,
    },
  }
}

export default useAuthPageTransition
